'use client';

import { useQuery } from '@tanstack/react-query';
import { LiquidationType } from '@velocity-exchange/sdk';
import { ENUM_UTILS, Serializer } from '@velocity-exchange/common';
import { JsonObject } from 'cerializr';
import StatsApiClient from 'src/utils/StatsApiClient';
import { createQueryKey } from 'src/utils/query/createQueryKey';

const TEN_MINUTES_MS = 10 * 60 * 1000;
const IF_STATS_QUERY_KEY = createQueryKey('insuranceFundStats');

export interface InsuranceFundStats {
	totalRevenue: number;
	totalPayment: number;
	totalSocializedLoss: number;
	usersSocialLoss: number;
	perpLiquidationTotal: number;
	spotLiquidationTotal: number;
	lastPerpBankruptcy: number;
	lastSpotBankruptcy: number;
	marketSharePriceData: Array<{
		marketIndex: number;
		apy: number;
	}>;
}

async function fetchInsuranceFundStats(): Promise<InsuranceFundStats | null> {
	const [insuranceFundStatsResult, bankruptcyStatsResult] = await Promise.all([
		StatsApiClient.fetchInsuranceFundStats(),
		StatsApiClient.fetchBankruptcies(),
	]);

	if (
		!insuranceFundStatsResult?.success ||
		!bankruptcyStatsResult?.success ||
		!insuranceFundStatsResult?.data
	) {
		return null;
	}

	// Derive last perp/spot bankruptcy timestamps from records
	let lastPerpBankruptcy = 0;
	let lastSpotBankruptcy = 0;

	for (const record of bankruptcyStatsResult.records ?? []) {
		try {
			const parsed = Serializer.Deserialize.DataApiUILiquidation(
				record as JsonObject
			);
			const isPerp = ENUM_UTILS.match(
				parsed.liquidationType,
				LiquidationType.PERP_BANKRUPTCY
			);
			// Keep timestamp in seconds - UI will multiply by 1000 for Date
			const timestamp = parsed.ts.toNumber();

			if (isPerp && lastPerpBankruptcy === 0) {
				lastPerpBankruptcy = timestamp;
			} else if (!isPerp && lastSpotBankruptcy === 0) {
				lastSpotBankruptcy = timestamp;
			}

			if (lastPerpBankruptcy !== 0 && lastSpotBankruptcy !== 0) break;
		} catch {
			// Skip records that fail to deserialize
		}
	}

	return {
		totalRevenue: Number(insuranceFundStatsResult.data.totalRevenue ?? 0),
		totalPayment: Number(bankruptcyStatsResult.stats?.ifPayment ?? 0),
		totalSocializedLoss: Number(bankruptcyStatsResult.stats?.socialLoss ?? 0),
		usersSocialLoss: 0, // Not available in new API
		perpLiquidationTotal: Number(
			insuranceFundStatsResult.data.perpLiqsTotal ?? 0
		),
		spotLiquidationTotal: Number(
			insuranceFundStatsResult.data.spotLiqsTotal ?? 0
		),
		lastPerpBankruptcy,
		lastSpotBankruptcy,
		marketSharePriceData: (
			insuranceFundStatsResult.data.marketSharePriceData ?? []
		).map((d) => ({
			marketIndex: d.marketIndex,
			apy: Number(d.apy ?? 0),
		})),
	};
}

export function useInsuranceFundStatsQuery() {
	return useQuery({
		queryKey: IF_STATS_QUERY_KEY,
		queryFn: fetchInsuranceFundStats,
		staleTime: TEN_MINUTES_MS,
		refetchInterval: TEN_MINUTES_MS,
	});
}
